import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { clearStoredToken } from '@/lib/auth';
import { ChartLine, MagnifyingGlass, Sparkle, Database, SignOut, TrendUp, Target, FileText, Eye, Crown, Rocket, Gear, BookOpen, Compass, Trophy, Wrench, Palette, Crosshair, Coin, Export, Users, Flame, Handshake } from '@phosphor-icons/react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import {
  CHART_TOOLTIP_STYLE,
  CHART_LINE_DOT,
  CHART_BAR_RADIUS,
} from '@/lib/constants';
import DashboardTour from '@/components/DashboardTour';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import BrandSwitcher from '@/components/BrandSwitcher';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PLAN_BADGE = {
  free: { label: 'Starter', color: '#9CA3AF', icon: null },
  pro_trial: { label: 'Pro Trial', color: '#10B981', icon: Rocket },
  pro: { label: 'Pro', color: '#2563EB', icon: Rocket },
  agency: { label: 'Agency', color: '#F59E0B', icon: Crown },
};

const DashboardLayout = ({ children, user, activePage }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [plan, setPlan] = useState('free');
  const [trial, setTrial] = useState(null);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/payments/me`, { withCredentials: true })
      .then((res) => {
        setPlan(res.data.plan || 'free');
        setTrial(res.data.trial || null);
      })
      .catch(() => {});
  }, []);

  const handleLogout = useCallback(async () => {
    try {
      await axios.post(
        `${BACKEND_URL}/api/auth/logout`,
        {},
        { withCredentials: true }
      );
    } catch {
      // Logout failed server-side - proceed to client redirect anyway
    }
    clearStoredToken();
    navigate('/');
  }, [navigate]);

  const menuItems = [
    { icon: ChartLine, label: t('nav.dashboard'), path: '/dashboard', testId: 'nav-dashboard' },
    { icon: MagnifyingGlass, label: t('nav.funnel_spy'), path: '/funnel-spy', testId: 'nav-funnel-spy' },
    { icon: Compass, label: t('nav.discovery'), path: '/discovery', testId: 'nav-discovery' },
    { icon: Trophy, label: t('nav.winner_score'), path: '/winner-score', testId: 'nav-winner-score' },
    { icon: Wrench, label: t('nav.auto_build'), path: '/auto-build', testId: 'nav-auto-build' },
    { icon: Rocket, label: t('nav.product_generator'), path: '/product-generator', testId: 'nav-product-generator' },
    { icon: Palette, label: t('nav.branding'), path: '/branding', testId: 'nav-branding' },
    { icon: Compass, label: t('nav.opportunities'), path: '/opportunities', testId: 'nav-opportunities' },
    { icon: Crosshair, label: t('nav.competitors'), path: '/competitors', testId: 'nav-competitors' },
    { icon: Coin, label: t('nav.credits'), path: '/credits', testId: 'nav-credits' },
    { icon: Handshake, label: t('nav.affiliates', 'Affiliates'), path: '/affiliates', testId: 'nav-affiliates' },
    { icon: Users, label: t('nav.crm'), path: '/crm', testId: 'nav-crm' },
    { icon: Flame, label: t('nav.heatmap'), path: '/heatmap', testId: 'nav-heatmap' },
    { icon: Export, label: t('nav.export_hub'), path: '/exports', testId: 'nav-exports' },
    { icon: Sparkle, label: t('nav.ai_composer'), path: '/ai-composer', testId: 'nav-ai-composer' },
    { icon: Eye, label: 'Monitor', path: '/monitoring', testId: 'nav-monitoring' },
    { icon: Database, label: t('nav.funnel_library'), path: '/library', testId: 'nav-library' },
    { icon: BookOpen, label: 'Guia', path: '/guia', testId: 'nav-guia' },
    { icon: Gear, label: t('nav.settings'), path: '/settings', testId: 'nav-settings' },
    // Founder-only — only rendered if user.is_founder (set by backend in /auth/me payload)
    ...(user?.is_founder ? [{ icon: Crown, label: 'Founder Usage', path: '/founder/usage', testId: 'nav-founder-usage', founderOnly: true }] : []),
  ];

  return (
    <div className="min-h-screen bg-[#050505] text-white flex">
      {/* Sidebar */}
      <aside className="w-64 bg-[#0A0A0A] border-r border-white/10 flex flex-col">
        <div className="p-6 border-b border-white/10">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 bg-gradient-to-br from-[#2563EB] to-[#3B82F6] rounded-lg flex items-center justify-center">
              <ChartLine size={24} weight="bold" className="text-white" />
            </div>
            <span className="text-xl font-[Outfit] font-bold tracking-tight">FunnelHack AI</span>
          </div>
        </div>

        <nav className="flex-1 p-4">
          <div className="space-y-2">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = activePage === item.path;
              return (
                <button
                  key={item.path}
                  data-testid={item.testId}
                  onClick={() => navigate(item.path)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                    isActive
                      ? 'bg-[#2563EB] text-white'
                      : 'text-white/60 hover:text-white hover:bg-white/10'
                  }`}
                >
                  <Icon size={20} weight="bold" />
                  <span className="font-medium">{item.label}</span>
                </button>
              );
            })}
          </div>
        </nav>

        <div className="p-4 border-t border-white/10">
          {/* Brand switcher (Agency Mode) */}
          <BrandSwitcher />

          {/* Plan badge */}
          <button
            onClick={() => navigate('/pricing')}
            data-testid="plan-badge"
            className="w-full mb-4 flex items-center justify-between gap-2 px-3 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg transition-all"
          >
            <div className="flex items-center gap-2 min-w-0">
              {PLAN_BADGE[plan]?.icon && React.createElement(PLAN_BADGE[plan].icon, {
                size: 16, weight: 'bold', style: { color: PLAN_BADGE[plan].color }
              })}
              <span className="text-xs uppercase tracking-[0.2em] font-medium truncate" style={{ color: PLAN_BADGE[plan]?.color || '#9CA3AF' }}>
                {PLAN_BADGE[plan]?.label || 'Starter'}
              </span>
            </div>
            {plan === 'pro_trial' && trial && (
              <span className="text-xs text-[#10B981]">{trial.days_remaining}d</span>
            )}
            {plan === 'free' && (
              <span className="text-xs text-[#2563EB] hover:text-[#3B82F6]">Upgrade</span>
            )}
          </button>

          <div className="flex items-center gap-3 mb-4">
            <img
              src={user?.picture || 'https://via.placeholder.com/40'}
              alt={user?.name}
              className="w-10 h-10 rounded-full flex-shrink-0"
            />
            <div className="flex-1 min-w-0 overflow-hidden">
              <p className="text-sm font-medium truncate">{user?.name}</p>
              <p className="text-xs text-white/40 truncate">{user?.email}</p>
            </div>
          </div>
          <div className="mb-2 px-1">
            <LanguageSwitcher variant="compact" />
          </div>
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            className="w-full flex items-center gap-2 px-4 py-2 text-white/60 hover:text-white hover:bg-white/10 rounded-lg transition-all duration-200"
          >
            <SignOut size={18} weight="bold" />
            <span className="text-sm">{t('common.logout')}</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        {plan === 'pro_trial' && trial && trial.days_remaining <= 7 && (
          <div
            data-testid="trial-banner"
            className="px-6 py-3 bg-gradient-to-r from-[#10B981]/20 to-[#2563EB]/10 border-b border-[#10B981]/30 flex items-center justify-between gap-4 flex-wrap"
          >
            <div className="flex items-center gap-2 text-sm">
              <Rocket size={18} weight="bold" className="text-[#10B981]" />
              <span className="text-white/90">
                <strong className="text-[#10B981]">{trial.days_remaining} dia{trial.days_remaining === 1 ? '' : 's'}</strong> restante{trial.days_remaining === 1 ? '' : 's'} do teu trial Pro
              </span>
            </div>
            <button
              onClick={() => navigate('/pricing')}
              data-testid="trial-upgrade-btn"
              className="px-4 py-1.5 bg-[#2563EB] hover:bg-[#3B82F6] rounded-lg text-sm font-medium transition-all"
            >
              Manter o Pro
            </button>
          </div>
        )}
        {children}
      </main>
    </div>
  );
};

const Dashboard = ({ user }) => {
  const { t, i18n } = useTranslation();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadStats = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/dashboard/stats`, {
        withCredentials: true
      });
      setStats(response.data);
    } catch {
      // Stats unavailable, render zeros
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const chartData = stats?.weekly_activity || [];
  const hasFunnels = (stats?.total_funnels || 0) > 0;

  if (loading) {
    return (
      <DashboardLayout user={user} activePage="/dashboard">
        <div className="flex items-center justify-center h-full">
          <div className="w-16 h-16 border-4 border-[#2563EB] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout user={user} activePage="/dashboard">
      <DashboardTour />
      <div className="p-6" data-testid="dashboard-page">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-[Outfit] font-bold tracking-tight mb-2">
            {t('dashboard.welcome_hi', { name: user?.name?.split(' ')[0] })}
          </h1>
          <p className="text-white/60">{t('dashboard.subtitle')}</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="p-6 bg-[#121212] border border-white/10 rounded-xl" data-testid="stat-funnels">
            <div className="flex items-center justify-between mb-2">
              <span className="text-white/60 text-sm uppercase tracking-[0.2em]">{t('dashboard.stat_funnels')}</span>
              <MagnifyingGlass size={20} className="text-[#2563EB]" weight="bold" />
            </div>
            <p className="text-3xl font-[Outfit] font-bold">{stats?.total_funnels || 0}</p>
            <p className="text-xs text-white/40 mt-1">{t('dashboard.stat_total_analyzed')}</p>
          </div>

          <div className="p-6 bg-[#121212] border border-white/10 rounded-xl" data-testid="stat-content">
            <div className="flex items-center justify-between mb-2">
              <span className="text-white/60 text-sm uppercase tracking-[0.2em]">{t('dashboard.stat_ai_content')}</span>
              <Sparkle size={20} className="text-[#2563EB]" weight="bold" />
            </div>
            <p className="text-3xl font-[Outfit] font-bold">{stats?.total_content || 0}</p>
            <p className="text-xs text-white/40 mt-1">{t('dashboard.stat_ai_generated')}</p>
          </div>

          <div className="p-6 bg-[#121212] border border-white/10 rounded-xl" data-testid="stat-conversion">
            <div className="flex items-center justify-between mb-2">
              <span className="text-white/60 text-sm uppercase tracking-[0.2em]">{t('dashboard.stat_conversion')}</span>
              <Target size={20} className="text-[#10B981]" weight="bold" />
            </div>
            <p className="text-3xl font-[Outfit] font-bold">
              {stats?.conversion_rate != null ? `${stats.conversion_rate}%` : '—'}
            </p>
            <p className="text-xs text-white/40 mt-1">
              {stats?.conversion_rate != null ? t('dashboard.stat_avg_est') : t('dashboard.stat_no_data')}
            </p>
          </div>

          <div className="p-6 bg-[#121212] border border-white/10 rounded-xl" data-testid="stat-ctr">
            <div className="flex items-center justify-between mb-2">
              <span className="text-white/60 text-sm uppercase tracking-[0.2em]">{t('dashboard.stat_ctr')}</span>
              <TrendUp size={20} className="text-[#2563EB]" weight="bold" />
            </div>
            <p className="text-3xl font-[Outfit] font-bold">
              {stats?.avg_ctr != null ? `${stats.avg_ctr}%` : '—'}
            </p>
            <p className="text-xs text-white/40 mt-1">
              {stats?.avg_ctr != null ? t('dashboard.stat_click_rate') : t('dashboard.stat_no_data')}
            </p>
          </div>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <div className="p-6 bg-[#121212] border border-white/10 rounded-xl">
            <h3 className="text-lg font-[Outfit] font-bold mb-4">{t('dashboard.chart_daily')}</h3>
            {hasFunnels ? (
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                  <XAxis dataKey="name" stroke="rgba(255,255,255,0.6)" />
                  <YAxis stroke="rgba(255,255,255,0.6)" allowDecimals={false} />
                  <Tooltip contentStyle={CHART_TOOLTIP_STYLE} />
                  <Line type="monotone" dataKey="funnels" stroke="#2563EB" strokeWidth={3} dot={CHART_LINE_DOT} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[200px] flex flex-col items-center justify-center text-center">
                <ChartLine size={40} className="text-white/20 mb-2" weight="bold" />
                <p className="text-white/40 text-sm">{t('dashboard.no_analyses')}</p>
                <p className="text-white/30 text-xs mt-1">{t('dashboard.no_analyses_hint')}</p>
              </div>
            )}
          </div>

          <div className="p-6 bg-[#121212] border border-white/10 rounded-xl">
            <h3 className="text-lg font-[Outfit] font-bold mb-4">{t('dashboard.chart_weekly')}</h3>
            {hasFunnels ? (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                  <XAxis dataKey="name" stroke="rgba(255,255,255,0.6)" />
                  <YAxis stroke="rgba(255,255,255,0.6)" allowDecimals={false} />
                  <Tooltip contentStyle={CHART_TOOLTIP_STYLE} />
                  <Bar dataKey="funnels" fill="#2563EB" radius={CHART_BAR_RADIUS} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[200px] flex flex-col items-center justify-center text-center">
                <TrendUp size={40} className="text-white/20 mb-2" weight="bold" />
                <p className="text-white/40 text-sm">{t('dashboard.no_perf')}</p>
                <p className="text-white/30 text-xs mt-1">{t('dashboard.no_perf_hint')}</p>
              </div>
            )}
          </div>
        </div>

        {/* Recent Funnels */}
        <div className="p-6 bg-[#121212] border border-white/10 rounded-xl">
          <h3 className="text-lg font-[Outfit] font-bold mb-4">{t('dashboard.recent_funnels')}</h3>
          {stats?.recent_funnels?.length > 0 ? (
            <div className="space-y-3">
              {stats.recent_funnels.map((funnel) => (
                <div
                  key={funnel.funnel_id}
                  data-testid={`funnel-${funnel.funnel_id}`}
                  className="flex items-center gap-4 p-4 bg-[#0A0A0A] rounded-lg hover:bg-white/5 transition-colors"
                >
                  <div className="w-10 h-10 bg-[#2563EB]/20 rounded-lg flex items-center justify-center">
                    <FileText size={20} className="text-[#2563EB]" weight="bold" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{funnel.title || t('common.untitled')}</p>
                    <p className="text-sm text-white/40 truncate">{funnel.url}</p>
                  </div>
                  <span className="text-xs text-white/40">
                    {new Date(funnel.created_at).toLocaleDateString(i18n.language === 'pt' ? 'pt-PT' : 'en-US')}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-white/40 text-center py-8">{t('dashboard.no_funnels_yet')}</p>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export { DashboardLayout };
export default Dashboard;